import { useState, useEffect } from "react";

const AddGroup = ({
  saveGroups,
  ghl_locations,
  selectedGroup,
  isEditMode,
  isDuplicateMode,
  resetGroupState,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  useEffect(() => {
    if ((isEditMode || isDuplicateMode) && selectedGroup) {
      setOpen(true);

      if (isEditMode) {
        setGroupName(selectedGroup.name || "");
      }

      const mappedLocations =
        selectedGroup.locations?.map((loc) => ({
          value: loc.id,
          label: loc.name,
        })) || [];

      setSelectedLocations(mappedLocations);

      if (isDuplicateMode) {
        setGroupName("");
        setSelectedLocations([]);
      }
    }
  }, [isEditMode, isDuplicateMode, selectedGroup]);

  const handleOpen = () => {
    if (!isEditMode) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedLocations([]);
    setGroupName("");
    setSearchTerm("");
    setShowLocationDropdown(false);
    resetGroupState();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      alert("Group name is required");
      return;
    }
    if (selectedLocations.length === 0) {
      alert("Please select at least one location");
      return;
    }

    const data = {
      name: groupName.trim(),
      locations: selectedLocations,
    };
    saveGroups(data);
    handleClose();
  };

  const handleLocationToggle = (location) => {
    setSelectedLocations((prev) => {
      const exists = prev.find((loc) => loc.value === location.value);
      if (exists) {
        return prev.filter((loc) => loc.value !== location.value);
      } else {
        return [...prev, location];
      }
    });
  };

  const removeLocation = (locationValue) => {
    setSelectedLocations((prev) =>
      prev.filter((loc) => loc.value !== locationValue)
    );
  };

  const filteredLocations = ghl_locations.filter((location) =>
    location.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableLocations = filteredLocations.filter(
    (location) =>
      !selectedLocations.find((selected) => selected.value === location.value)
  );

  const title = isEditMode
    ? "Edit group"
    : isDuplicateMode
    ? "Duplicate group"
    : "Add new group";

  const submitLabel = isEditMode
    ? "Update group"
    : isDuplicateMode
    ? "Duplicate group"
    : "Save group";

  return (
    <>
      {!isEditMode && (
        <button
          type="button"
          className="ag-trigger d-inline-flex align-items-center"
          onClick={handleOpen}
        >
          <span className="ag-trigger-icon d-inline-flex align-items-center justify-content-center" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </span>
          <span>Add new group</span>
        </button>
      )}

      {open && (
        <div className="ag-overlay" onMouseDown={handleClose}>
          <div
            className="ag-dialog"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit}>
              <header className="ag-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="ag-title mb-0">{title}</h5>
                  <p className="ag-subtitle mb-0">
                    Group locations together to customize their UI in one place.
                  </p>
                </div>
                <button
                  type="button"
                  className="ag-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </header>

              <div className="ag-body">
                <div className="ag-field">
                  <label className="ag-label">Group name</label>
                  <input
                    type="text"
                    className="ag-input"
                    placeholder="e.g. Premium accounts"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    required
                  />
                </div>

                <div className="ag-field mb-0">
                  <label className="ag-label d-flex justify-content-between align-items-center">
                    <span>Locations</span>
                    {selectedLocations.length > 0 && (
                      <span className="ag-count">
                        {selectedLocations.length} selected
                      </span>
                    )}
                  </label>

                  {selectedLocations.length > 0 && (
                    <div className="ag-selected d-flex flex-wrap gap-1 mb-2">
                      {selectedLocations.map((location) => (
                        <span key={location.value} className="ag-pill">
                          <span className="ag-pill-label">{location.label}</span>
                          <button
                            type="button"
                            className="ag-pill-remove"
                            onClick={() => removeLocation(location.value)}
                            aria-label={`Remove ${location.label}`}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="ag-search-wrap position-relative">
                    <span className="ag-search-icon d-flex align-items-center justify-content-center" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="ag-input ag-input--search"
                      placeholder="Search locations"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onFocus={() => setShowLocationDropdown(true)}
                    />
                    <button
                      type="button"
                      className="ag-search-toggle"
                      onClick={() => setShowLocationDropdown((p) => !p)}
                      aria-label="Toggle dropdown"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: showLocationDropdown ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.18s ease",
                        }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {showLocationDropdown && (
                      <div className="ag-dropdown">
                        {availableLocations.length === 0 ? (
                          <div className="ag-dropdown-empty">
                            {searchTerm ? "No locations match your search." : "All locations are already selected."}
                          </div>
                        ) : (
                          availableLocations.map((location) => (
                            <button
                              key={location.value}
                              type="button"
                              className="ag-dropdown-item d-flex align-items-center justify-content-between"
                              onClick={() => {
                                handleLocationToggle(location);
                                setSearchTerm("");
                              }}
                            >
                              <span className="text-truncate">{location.label}</span>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ag-dropdown-add">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <footer className="ag-footer d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="ag-btn ag-btn--ghost"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ag-btn ag-btn--primary"
                  disabled={!selectedLocations.length || !groupName.trim()}
                >
                  {submitLabel}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      <style>{`
        /* Trigger */
        .ag-trigger {
          position: relative;
          font-family: 'Inter', sans-serif;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 8px 16px 8px 8px;
          border-radius: 9px;
          border: 1px solid rgba(255, 255, 255, 0.04);
          line-height: 1;
          cursor: pointer;
          gap: 8px;
          color: #fff;
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.25),
            0 6px 14px rgba(79, 70, 229, 0.22);
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease;
        }
        .ag-trigger:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.3),
            0 10px 22px rgba(79, 70, 229, 0.32);
        }
        .ag-trigger:hover .ag-trigger-icon {
          background: rgba(255, 255, 255, 0.26);
          transform: rotate(90deg);
        }
        .ag-trigger:active {
          transform: translateY(0);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(67, 56, 202, 0.3);
        }
        .ag-trigger:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2), 0 6px 14px rgba(79, 70, 229, 0.28);
        }
        .ag-trigger-icon {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.18);
          color: #fff;
          flex-shrink: 0;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        /* Overlay & dialog */
        .ag-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(2px);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          font-family: 'Inter', sans-serif;
          animation: agFade 0.18s ease;
        }
        @keyframes agFade { from { opacity: 0; } to { opacity: 1; } }
        .ag-dialog {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #ececec;
          width: 100%;
          max-width: 540px;
          max-height: calc(100vh - 48px);
          display: flex;
          flex-direction: column;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
          animation: agPop 0.18s ease;
          overflow: hidden;
        }
        @keyframes agPop {
          from { opacity: 0; transform: scale(0.97) translateY(6px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .ag-header { padding: 18px 22px 14px; }
        .ag-title {
          font-size: 1.0625rem;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: -0.005em;
        }
        .ag-subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-top: 2px !important;
        }
        .ag-close {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          background: transparent;
          border: 0;
          color: #6b7280;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .ag-close:hover { background: #f3f4f6; color: #1f2937; }
        .ag-body { padding: 6px 22px 18px; overflow-y: auto; }
        .ag-footer {
          padding: 14px 22px 18px;
          border-top: 1px solid #f1f1f1;
          background: #fff;
        }

        /* Fields */
        .ag-field { margin-bottom: 16px; }
        .ag-label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 6px;
        }
        .ag-count {
          font-size: 0.6875rem;
          font-weight: 500;
          color: #4f46e5;
          background: #eef2ff;
          border: 1px solid #e0e7ff;
          padding: 2px 8px;
          border-radius: 999px;
        }
        .ag-input {
          width: 100%;
          height: 38px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 0 12px;
          background: #fff;
          font-size: 0.875rem;
          color: #1f2937;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          font-family: inherit;
        }
        .ag-input::placeholder { color: #9ca3af; }
        .ag-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        .ag-input--search { padding-left: 36px; padding-right: 36px; }
        .ag-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          pointer-events: none;
        }
        .ag-search-toggle {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border: 0;
          background: transparent;
          color: #6b7280;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .ag-search-toggle:hover { background: #f3f4f6; color: #1f2937; }

        /* Selected location pills */
        .ag-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 4px 4px 10px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #4338ca;
          background: #eef2ff;
          border: 1px solid #e0e7ff;
          border-radius: 999px;
          line-height: 1.4;
        }
        .ag-pill-remove {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: rgba(79, 70, 229, 0.1);
          color: #4f46e5;
          border: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .ag-pill-remove:hover {
          background: #4f46e5;
          color: #fff;
        }

        /* Dropdown */
        .ag-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(17, 24, 39, 0.08);
          max-height: 220px;
          overflow-y: auto;
          z-index: 1060;
          padding: 4px;
          animation: agDropFade 0.14s ease;
        }
        @keyframes agDropFade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ag-dropdown-empty {
          padding: 14px;
          text-align: center;
          font-size: 0.8125rem;
          color: #9ca3af;
        }
        .ag-dropdown-item {
          width: 100%;
          padding: 9px 10px;
          font-size: 0.875rem;
          color: #1f2937;
          background: transparent;
          border: 0;
          border-radius: 7px;
          text-align: left;
          cursor: pointer;
          transition: background 0.12s ease, color 0.12s ease;
        }
        .ag-dropdown-item:hover {
          background: #f5f3ff;
          color: #4338ca;
        }
        .ag-dropdown-add {
          color: #9ca3af;
          flex-shrink: 0;
          transition: color 0.12s ease;
        }
        .ag-dropdown-item:hover .ag-dropdown-add { color: #4f46e5; }

        /* Buttons */
        .ag-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 8px 16px;
          border-radius: 9px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .ag-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none !important; }
        .ag-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2), 0 4px 10px rgba(79, 70, 229, 0.18);
        }
        .ag-btn--ghost {
          background: #fff;
          border-color: #e5e7eb;
          color: #1f2937;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
        }
        .ag-btn--ghost:hover:not(:disabled) {
          background: #fafafa;
          border-color: #d1d5db;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
        }
        .ag-btn--ghost:active:not(:disabled) { transform: translateY(0); }

        .ag-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .ag-btn--primary:hover:not(:disabled) {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .ag-btn--primary:active:not(:disabled) {
          transform: translateY(0);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(67, 56, 202, 0.25);
        }
      `}</style>
    </>
  );
};

export default AddGroup;
